import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../api/client'
import { Logo } from '../../components/Logo'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { STAGES, bandFromScore, displayName, stageIndex } from '../../lib/format'
import type { Brief, CaseStudy, MeetingPrep, StageId } from '../../types'
import { BriefBody } from './BriefBody'
import { Followup } from './Followup'
import { MeetingPrepEditor } from './MeetingPrepEditor'
import { SignalsPanel, StakeholdersPanel } from './Panels'
import { Gauge, MeetingPrepPanel } from './parts'

export function AccountView() {
  const { company = '' } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = useAuth()

  const [brief, setBrief] = useState<Brief | null>(null)
  const [cases, setCases] = useState<CaseStudy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingPrep, setEditingPrep] = useState(false)
  const [extraTab, setExtraTab] = useState<'stake' | 'sig'>('stake')

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([api.brief(company), api.caseStudies()])
      .then(([b, c]) => {
        setBrief(b)
        setCases(c)
        setError('')
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [company])

  useEffect(load, [load])

  const canEdit = can('RESEARCHER')

  async function setStage(stage: StageId) {
    if (!canEdit) return toast('Read-only access — you need the Researcher role')
    try {
      setBrief(await api.setStage(company, stage))
      toast(`Stage: ${STAGES[stageIndex(stage)].label}`)
      // Scroll the newly-current stage into view, as the prototype did.
      window.setTimeout(() => document.getElementById(`flow-${stage}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not change stage')
    }
  }

  async function savePrep(prep: MeetingPrep) {
    try {
      setBrief(await api.saveMeetingPrep(company, prep))
      setEditingPrep(false)
      const empty = !prep.attendee && !prep.role && !prep.meta && !prep.notes
      toast(empty ? 'Meeting prep removed' : 'Meeting prep saved')
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not save prep')
    }
  }

  if (loading) {
    return (
      <div className="loading on">
        <div className="ring" />
        <div className="lmsg">Loading account…</div>
      </div>
    )
  }

  if (error || !brief) {
    return (
      <div className="empty">
        <div className="ico">⌕</div>
        {error || 'Account not found.'}
        <div style={{ marginTop: 12 }}>
          <button className="tb-btn" onClick={() => navigate('/accounts')}>
            ← All accounts
          </button>
        </div>
      </div>
    )
  }

  const score = brief.score ?? 0
  const band = brief.full.band ?? bandFromScore(score)
  const current = stageIndex(brief.stage)
  const prep = brief.full.meetingPrep

  const stageState = (i: number) => (i < current ? 'done' : i === current ? 'on' : 'todo')

  return (
    <div className="view on">
      <div className="acct-head">
        <button className="acct-back" onClick={() => navigate('/accounts')}>
          <i className="ti ti-arrow-left" /> All accounts
        </button>
        <div className="acct-head-row">
          <div className="acct-meta">
            <div className="acct-sector">{brief.sector}</div>
            <div className="acct-name-row">
              <Logo company={brief.company} logo={brief.logo} size={40} />
              <div className="acct-name">{displayName(brief.company)}</div>
            </div>
            <div className="acct-web">
              {brief.website && (
                <>
                  <a
                    href={`https://${brief.website.replace(/^https?:\/\//, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {brief.website}
                  </a>{' '}
                  ·{' '}
                </>
              )}
              Account brief
            </div>
          </div>

          <div className="acct-gauge">
            <Gauge score={score} band={band} size={100} />
          </div>

          <div className="acct-utils">
            <button className="ghost-btn" onClick={() => window.print()} title="Print / PDF">
              <i className="ti ti-printer" />
            </button>
          </div>
        </div>
      </div>

      <div className="stagebar-sticky">
        <div className="stagebar">
          {STAGES.map((s, i) => (
            <span key={s.id} style={{ display: 'contents' }}>
              <button
                className={`stage ${stageState(i)}`}
                title={s.hint}
                onClick={() => setStage(s.id)}
                disabled={!canEdit}
              >
                <span className="st-dot">{i < current ? '✓' : i + 1}</span>
                <span className="st-lbl">{s.label}</span>
              </button>
              {i < STAGES.length - 1 && (
                <span className={`st-arrow ${i < current ? 'done' : ''}`}>→</span>
              )}
            </span>
          ))}
        </div>
      </div>

      {editingPrep && (
        <MeetingPrepEditor
          company={brief.company}
          prep={prep}
          onSave={savePrep}
          onCancel={() => setEditingPrep(false)}
        />
      )}

      {/* ── Stage 1 · Discover: the full brief, inline ───────────────────────── */}
      <FlowSection id="discover" index={0} current={current} hint="Brief generated">
        <BriefBody brief={brief} cases={cases} />
      </FlowSection>

      {/* ── Stage 2 · Assess: meeting prep ──────────────────────────────────── */}
      <FlowSection id="assess" index={1} current={current} hint="Meeting prep & capture">
        {prep ? (
          <>
            <p className="flow-p">
              Meeting prep is ready. Review your talking points, stakeholders and questions before the
              meeting, then capture what happened in HubSpot.
            </p>
            <div className="flow-actions">
              <button className="tb-btn" onClick={() => setEditingPrep(true)} disabled={!canEdit}>
                <i className="ti ti-edit" style={{ fontSize: 14 }} /> Edit meeting prep
              </button>
            </div>
            <div className="flow-prepwrap">
              <MeetingPrepPanel prep={prep} />
            </div>
          </>
        ) : (
          <>
            <p className="flow-p">
              No meeting prep yet. Add talking points, stakeholders and questions to prepare for the meeting.
            </p>
            <div className="flow-actions">
              <button
                className="genbtn"
                style={{ margin: 0, padding: '10px 18px' }}
                onClick={() => setEditingPrep(true)}
                disabled={!canEdit}
              >
                <i className="ti ti-plus" style={{ fontSize: 15 }} /> Add meeting prep
              </button>
            </div>
          </>
        )}
      </FlowSection>

      {/* ── Stage 3 · Qualify: the follow-up email ──────────────────────────── */}
      <FlowSection id="qualify" index={2} current={current} hint="Follow-up email">
        <Followup brief={brief} onGenerated={setBrief} canEdit={canEdit} />
      </FlowSection>

      {/* ── Stage 4 · Pipeline ──────────────────────────────────────────────── */}
      <FlowSection id="pipeline" index={3} current={current} hint="Live opportunity">
        <p className="flow-p">
          {brief.stage === 'pipeline'
            ? 'This account is live in the pipeline. Keep momentum with the wider team and track it in HubSpot.'
            : "Once it's a go, move the account to Pipeline using the stage bar above. It then shows as a live opportunity."}
        </p>
        {brief.stage === 'pipeline' && (
          <div className="flow-actions">
            <button className="tb-btn" onClick={() => navigate('/accounts')}>
              <i className="ti ti-chart-bar" style={{ fontSize: 14 }} /> View in accounts
            </button>
          </div>
        )}
      </FlowSection>

      <section className="flow-extra">
        <div className="flow-extra-h">
          <i className="ti ti-users" /> Stakeholders &amp; signals
        </div>
        <div className="flow-extra-tabs">
          <button
            className={`fx-tab${extraTab === 'stake' ? ' on' : ''}`}
            onClick={() => setExtraTab('stake')}
          >
            Stakeholders
          </button>
          <button className={`fx-tab${extraTab === 'sig' ? ' on' : ''}`} onClick={() => setExtraTab('sig')}>
            Signals
          </button>
        </div>
        {extraTab === 'stake' ? <StakeholdersPanel body={brief.full} /> : <SignalsPanel body={brief.full} />}
      </section>
    </div>
  )
}

/** One numbered stage block in the account flow. done / on ("You are here") / todo. */
function FlowSection({
  id,
  index,
  current,
  hint,
  children,
}: {
  id: StageId
  index: number
  current: number
  hint: string
  children: React.ReactNode
}) {
  const state = index < current ? 'done' : index === current ? 'on' : 'todo'
  const stage = STAGES[index]

  return (
    <section className={`flow-sec ${state}`} id={`flow-${id}`}>
      <div className="flow-h">
        <span className="flow-num">{state === 'done' ? '✓' : index + 1}</span>
        <span className="flow-title">{stage.label}</span>
        <span className="flow-hint">{hint}</span>
        {state === 'on' && <span className="flow-here">You are here</span>}
        {state === 'todo' && <span className="flow-next">Upcoming</span>}
        {state === 'done' && <span className="flow-donelbl">Done</span>}
      </div>
      <div className="flow-body">{children}</div>
    </section>
  )
}
